import React, { useState } from "react";
import hosys from "@/api/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Pencil, Trash2, Plus, GraduationCap, Upload, Loader2, ExternalLink, Search } from "lucide-react";
import moment from "moment";

const TRAINING_TYPES = {
  initial: "Inicial (incorporació)",
  periodic: "Periòdica",
  specific: "Específica (situació concreta)",
  online: "Online",
  presential: "Presencial",
};

const TRAINING_TOPICS = [
  "Prevenció de l'assetjament laboral",
  "Prevenció de l'assetjament sexual",
  "Igualtat de gènere",
  "Resolució de conflictes",
  "Protocol intern d'assetjament",
  "Drets i obligacions laborals",
];

const BLANK = {
  worker_name: "", worker_email: "", training_type: "presential",
  training_date: new Date().toISOString().split("T")[0], expiry_date: "",
  duration_hours: "", trainer: "", topics: [], certificate_url: "", notes: "",
};

export default function HarassmentTrainingList({ items, venueId, onChanged, isAdmin }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState("");

  const openForm = (item = null) => {
    setEditing(item);
    setForm(item ? { ...BLANK, ...item } : BLANK);
    setShowForm(true);
  };

  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));

  const toggleTopic = (t) => {
    const topics = form.topics || [];
    set("topics", topics.includes(t) ? topics.filter(x => x !== t) : [...topics, t]);
  };

  const handleUploadCert = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const { file_url } = await hosys.integrations.Core.UploadFile({ file });
    set("certificate_url", file_url);
    setUploading(false);
  };

  const handleSave = async () => {
    if (!form.worker_name || !form.training_date) return;
    setSaving(true);
    if (editing?.id) {
      await hosys.entities.HarassmentTraining.update(editing.id, { ...form, venue_id: venueId });
    } else {
      await hosys.entities.HarassmentTraining.create({ ...form, venue_id: venueId });
    }
    setSaving(false);
    setShowForm(false);
    onChanged();
  };

  const handleDelete = async (id) => {
    await hosys.entities.HarassmentTraining.delete(id);
    onChanged();
  };

  const filtered = items.filter(i => !search || i.worker_name?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input placeholder="Cercar treballador..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
        {isAdmin && (
          <Button onClick={() => openForm()} className="bg-[#7B2D3F] hover:bg-[#5B1D2F] gap-2 shrink-0">
            <Plus className="w-4 h-4" /> Afegir formació
          </Button>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          <GraduationCap className="w-10 h-10 mx-auto mb-2 text-gray-200" />
          <p className="text-sm">Sense registres de formació</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(item => {
            const expired = item.expiry_date && moment(item.expiry_date).isBefore(moment(), "day");
            const expiringSoon = item.expiry_date && !expired && moment(item.expiry_date).isBefore(moment().add(30, "days"), "day");
            return (
              <Card key={item.id} className="p-4 border-0 shadow-sm hover:shadow-md transition-all">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-purple-50 flex items-center justify-center flex-shrink-0">
                    <GraduationCap className="w-4 h-4 text-purple-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900 text-sm">{item.worker_name}</span>
                      <Badge className="bg-purple-100 text-purple-700 border-0 text-[10px]">{TRAINING_TYPES[item.training_type]}</Badge>
                      {expired && <Badge className="bg-red-100 text-red-700 border-0 text-[10px]">Caducada</Badge>}
                      {expiringSoon && <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px]">Caduca aviat</Badge>}
                    </div>
                    <div className="flex flex-wrap gap-x-3 text-[11px] text-gray-400 mt-0.5">
                      <span>{moment(item.training_date).format("DD/MM/YYYY")}</span>
                      {item.duration_hours && <span>· {item.duration_hours}h</span>}
                      {item.trainer && <span>· {item.trainer}</span>}
                      {item.expiry_date && <span>· Caduca: {moment(item.expiry_date).format("DD/MM/YYYY")}</span>}
                    </div>
                    {(item.topics || []).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {item.topics.slice(0, 3).map(t => (
                          <span key={t} className="text-[10px] bg-gray-100 text-gray-500 rounded-full px-2 py-0.5">{t}</span>
                        ))}
                        {item.topics.length > 3 && <span className="text-[10px] text-gray-400">+{item.topics.length - 3}</span>}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    {item.certificate_url && (
                      <a href={item.certificate_url} target="_blank" rel="noopener noreferrer">
                        <Button variant="ghost" size="icon" className="w-7 h-7 text-blue-400 hover:text-blue-600"><ExternalLink className="w-3.5 h-3.5" /></Button>
                      </a>
                    )}
                    {isAdmin && (
                      <>
                        <Button variant="ghost" size="icon" className="w-7 h-7 text-gray-400 hover:text-[#7B2D3F]" onClick={() => openForm(item)}><Pencil className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="icon" className="w-7 h-7 text-gray-400 hover:text-red-600" onClick={() => handleDelete(item.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={o => { if (!o) setShowForm(false); }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar formació" : "Registrar formació"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nom del treballador *</Label>
                <Input value={form.worker_name} onChange={e => set("worker_name", e.target.value)} placeholder="Nom complet" />
              </div>
              <div>
                <Label>Email (opcional)</Label>
                <Input type="email" value={form.worker_email} onChange={e => set("worker_email", e.target.value)} placeholder="email@exemple.com" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tipus de formació *</Label>
                <Select value={form.training_type} onValueChange={v => set("training_type", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(TRAINING_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Data de la formació *</Label>
                <Input type="date" value={form.training_date} onChange={e => set("training_date", e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Data de caducitat</Label>
                <Input type="date" value={form.expiry_date} onChange={e => set("expiry_date", e.target.value)} />
              </div>
              <div>
                <Label>Durada (hores)</Label>
                <Input type="number" value={form.duration_hours} onChange={e => set("duration_hours", e.target.value)} placeholder="Ex: 2" min={0} />
              </div>
            </div>
            <div>
              <Label>Formador / Entitat</Label>
              <Input value={form.trainer} onChange={e => set("trainer", e.target.value)} placeholder="Nom del formador o empresa..." />
            </div>
            <div>
              <Label>Temes coberts</Label>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {TRAINING_TOPICS.map(t => (
                  <button key={t} onClick={() => toggleTopic(t)}
                    className={`text-xs rounded-full px-2.5 py-1 border transition-colors ${
                      (form.topics || []).includes(t) ? "bg-purple-100 text-purple-700 border-purple-200 font-medium" : "bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100"
                    }`}>{t}</button>
                ))}
              </div>
            </div>
            <div>
              <Label>Certificat (opcional)</Label>
              <div className="flex items-center gap-3 mt-1">
                <label className="flex items-center gap-2 cursor-pointer bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 transition-colors">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {uploading ? "Pujant..." : "Adjuntar certificat"}
                  <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={handleUploadCert} disabled={uploading} />
                </label>
                {form.certificate_url && <span className="text-xs text-emerald-600 font-medium">✓ Certificat pujat</span>}
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={e => set("notes", e.target.value)} rows={2} placeholder="Observacions..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel·lar</Button>
            <Button onClick={handleSave} disabled={!form.worker_name || !form.training_date || saving} className="bg-[#7B2D3F] hover:bg-[#5B1D2F]">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editing ? "Actualitzar" : "Registrar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}